
import React, { useState } from 'react';
import PageLayout from '@/components/PageLayout';
import { useSettings } from '@/contexts/SettingsContext';
import { useAuth } from '@/contexts/AuthProvider';
import { useI18n } from '@/i18n/LanguageContext';
import { Globe, Bell, Share2, LogOut, Download } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { createRepairShop, getRepairShopByPlaceId } from '@/services/repairShops';
import { useToast } from '@/hooks/use-toast';

const SettingsPage = () => {
  const { settings, updateSettings } = useSettings();
  const { t } = useI18n();
  const { signOut } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [importing, setImporting] = useState(false);
  
  const KEY_PROVIDERS = 'ow_service_providers_v1';
  
  const handleImportLocalStorage = async () => {
    try {
      setImporting(true);
      const stored = localStorage.getItem(KEY_PROVIDERS);
      if (!stored) {
        toast({
          title: t('settings.import.noData') || 'No saved shops found',
          description: t('settings.import.noDataDesc') || 'No repair shops found in browser storage.',
        });
        return;
      }
      
      const providers = JSON.parse(stored);
      if (!Array.isArray(providers) || providers.length === 0) {
        toast({
          title: t('settings.import.noData') || 'No saved shops found',
          description: t('settings.import.noDataDesc') || 'No repair shops found in browser storage.',
        });
        return;
      }
      
      const { data: userData } = await supabase.auth.getUser();
      const uid = userData.user?.id;
      if (!uid) {
        toast({
          title: t('settings.import.error') || 'Error',
          description: t('settings.import.notLoggedIn') || 'Please log in to import shops.',
          variant: 'destructive',
        });
        return;
      }
      
      let imported = 0;
      let skipped = 0;
      
      for (const p of providers) {
        try {
          // Skip if no place_id (can't match with Google Places)
          if (!p.location && !p.place_id) {
            skipped++;
            continue;
          }
          
          // Check if already exists
          if (p.place_id) {
            const existing = await getRepairShopByPlaceId(p.place_id);
            if (existing) {
              skipped++;
              continue;
            }
          }
          
          // Create repair shop (without place_id if not available)
          await createRepairShop(uid, {
            place_id: p.place_id || undefined,
            name: p.name || 'Imported shop',
            formatted_address: p.location || undefined,
            phone: p.phone || undefined,
            mechanic_name: p.mechanic || undefined,
            lat: p.lat || 56.9496, // Default to Riga if no coords
            lng: p.lng || 24.1052,
          } as any);
          imported++;
        } catch (e) {
          console.warn('Failed to import shop:', p, e);
          skipped++;
        }
      }
      
      // Clear localStorage after successful import
      localStorage.removeItem(KEY_PROVIDERS);
      
      toast({
        title: t('settings.import.success') || 'Import complete',
        description: t('settings.import.successDesc', { imported, skipped }) || `${imported} shops imported, ${skipped} skipped.`,
      });
    } catch (e) {
      toast({
        title: t('settings.import.error') || 'Import failed',
        description: e instanceof Error ? e.message : 'Failed to import shops.',
        variant: 'destructive',
      });
    } finally {
      setImporting(false);
    }
  };
  
  const handleLogout = async () => {
    try {
      await signOut();
      navigate('/');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };
  
  return (
    <PageLayout title={t('settings.title')}>
      <div className="space-y-6">
        {/* Language Settings */}
        <div className="bg-white dark:bg-gray-800 rounded-xl p-4 shadow-sm">
          <div className="flex items-center space-x-3 mb-4">
            <Globe size={20} className="text-gray-500" />
            <h2 className="text-lg font-semibold">{t('settings.language')}</h2>
          </div>
          
          <div className="space-y-2">
            <label className="flex items-center space-x-3 p-2 hover:bg-gray-50 dark:hover:bg-gray-750 rounded-lg">
              <input
                type="radio"
                name="language"
                value="en"
                checked={settings.language === 'en'}
                onChange={() => updateSettings({ language: 'en' })}
                className="w-4 h-4 accent-lime"
              />
              <span>{t('settings.english')}</span>
            </label>
            
            <label className="flex items-center space-x-3 p-2 hover:bg-gray-50 dark:hover:bg-gray-750 rounded-lg">
              <input
                type="radio"
                name="language"
                value="lv"
                checked={settings.language === 'lv'}
                onChange={() => updateSettings({ language: 'lv' })}
                className="w-4 h-4 accent-lime"
              />
              <span>{t('settings.latvian')}</span>
            </label>
          </div>
        </div>
        
        {/* Notifications */}
        <div className="bg-white dark:bg-gray-800 rounded-xl p-4 shadow-sm">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <Bell size={20} className="text-gray-500" />
              <h2 className="text-lg font-semibold">{t('settings.notifications')}</h2>
            </div>
            
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                className="sr-only peer"
                checked={settings.notificationsEnabled}
                onChange={() => updateSettings({ notificationsEnabled: !settings.notificationsEnabled })}
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-lime"></div>
            </label>
          </div>
        </div>
        
        {/* Mechanic Link Sharing */}
        <div className="bg-white dark:bg-gray-800 rounded-xl p-4 shadow-sm">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center space-x-3">
              <Share2 size={20} className="text-gray-500" />
              <h2 className="text-lg font-semibold">{t('settings.mechanicSharing')}</h2>
            </div>
            
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                className="sr-only peer"
                checked={settings.mechanicSharingEnabled}
                onChange={() => updateSettings({ mechanicSharingEnabled: !settings.mechanicSharingEnabled })}
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-lime"></div>
            </label>
          </div>
          
          <p className="text-sm text-gray-500">
            {t('settings.mechanicSharingDesc')}
          </p>
        </div>
        
        {/* Advanced: Import LocalStorage Shops */}
        <div className="bg-white dark:bg-gray-800 rounded-xl p-4 shadow-sm">
          <div className="flex items-center space-x-3 mb-4">
            <Download size={20} className="text-gray-500" />
            <h2 className="text-lg font-semibold">{t('settings.advanced') || 'Advanced'}</h2>
          </div>
          
          <Button 
            onClick={handleImportLocalStorage}
            disabled={importing}
            variant="outline"
            className="w-full"
          >
            {importing 
              ? (t('settings.import.importing') || 'Importing...')
              : (t('settings.import.button') || 'Import saved repair shops')
            }
          </Button>
          <p className="text-xs text-gray-500 mt-2">
            {t('settings.import.hint') || 'Import repair shops saved in your browser storage to your account.'}
          </p>
        </div>
        
        {/* Logout Button Section */}
        <div className="bg-white dark:bg-gray-800 rounded-xl p-4 shadow-sm">
          <div className="flex items-center space-x-3 mb-4">
            <LogOut size={20} className="text-gray-500" />
            <h2 className="text-lg font-semibold">{t('settings.logout')}</h2>
          </div>
          
          <Button 
            onClick={handleLogout}
            variant="outline"
            className="w-full border-red-300 text-red-600 hover:bg-red-50 hover:text-red-700 dark:border-red-800 dark:text-red-500 dark:hover:bg-red-950"
          >
            {t('settings.logoutButton')}
          </Button>
        </div>
      </div>
    </PageLayout>
  );
};

export default SettingsPage;
